"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Hammer, Ruler } from "lucide-react";
const milestones = [{
  year: "2022",
  title: "Foundation of Excellence",
  description: "Established SolidTrust Builders as a reliable building contractor in North West London, focusing on high-quality single storey extensions and foundational groundwork.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586109/img/single_storey_extension_nw_london.png"
}, {
  year: "2023",
  title: "Mastering the Loft",
  description: "Expanded our architectural expertise to become a premier loft conversion specialist, delivering bespoke dormer and attic conversions with uncompromising precision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586109/img/dormer_loft_conversion_specialist.png"
}, {
  year: "2024",
  title: "Architectural Expansion",
  description: "Undertook complex double storey extensions and side return extensions, redefining luxury living spaces and solidifying our reputation among the best builders in North West London.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586109/img/double_storey_extension_luxury.png"
}, {
  year: "2025",
  title: "Full Property Transformation",
  description: "Executing comprehensive home renovations and house refurbishments. From kitchen renovation to full structural overhauls, we deliver unparalleled craftsmanship.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586109/img/full_property_renovation_london.png"
}];
export default function StructuralMilestones() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="structural-milestones" className="dark bg-background text-foreground py-24 md:py-32 relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {}
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Ruler className="w-6 h-6 text-primary" />
            <h2 className="text-sm font-mono tracking-[0.3em] uppercase text-muted-foreground">
              SolidTrust Builders
            </h2>
            <Hammer className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-4xl md:text-6xl font-serif font-bold tracking-tighter uppercase mb-6">
            Structural <span className="text-transparent bg-clip-text bg-gradient-to-b from-primary to-muted-foreground">Milestones</span>
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed">
            A chronological blueprint of our legacy as local home extension specialists in North West London.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-[2px] bg-primary origin-top transition-transform duration-[1500ms] ease-in-out z-0" style={{
          transform: isVisible ? 'scaleY(1)' : 'scaleY(0)'
        }} />

          {}
          <div className="space-y-16 md:space-y-32">
            {milestones.map((item, i) => <div key={i} data-index={i} className={`relative flex flex-col md:flex-row items-start md:items-center justify-between w-full group ${i % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                {}
                <div className="absolute left-6 md:left-1/2 transform -translate-x-1/2 w-4 h-4 bg-background border-2 border-primary rotate-45 z-10 transition-all duration-500 group-hover:scale-150 group-hover:bg-primary mt-2 md:mt-0"></div>

                {}
                <div className="w-full pl-16 md:pl-0 md:w-[45%] flex flex-col gap-6">
                  
                  {}
                  <div className={`flex ${i % 2 === 0 ? 'md:justify-start' : 'md:justify-end'}`}>
                    <Badge variant="outline" className="text-xl md:text-2xl py-2 px-6 border-2 border-primary rounded-none font-mono tracking-widest bg-background text-primary shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] transition-transform group-hover:-translate-y-1 group-hover:-translate-x-1">
                      {item.year}
                    </Badge>
                  </div>

                  {}
                  <div className="relative p-8 md:p-10 border-2 border-primary bg-card overflow-hidden transition-all duration-500 hover:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.1)]">
                    {}
                    <div className="absolute inset-0 opacity-10 grayscale mix-blend-screen transition-transform duration-1000 group-hover:scale-110 group-hover:opacity-20">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    
                    <div className="relative z-10">
                      <h4 className="text-2xl md:text-3xl font-serif font-bold mb-4 uppercase tracking-tight text-foreground">
                        {item.title}
                      </h4>
                      <div className="w-12 h-[2px] bg-primary mb-6 transition-all duration-500 group-hover:w-full"></div>
                      <p className="text-muted-foreground leading-relaxed text-base md:text-lg">
                        {item.description}
                      </p>
                    </div>
                  </div>

                </div>
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-32 text-center relative z-10">
          <div className="inline-flex flex-col items-center gap-6 p-8 border-2 border-primary bg-card/50 backdrop-blur-sm !w-[379.047px]">
            <h4 className="text-2xl font-serif uppercase tracking-tight">Ready to build your legacy?</h4>
            <Button asChild size="lg" className="rounded-none text-lg px-8 py-6 uppercase tracking-widest font-bold group">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary text-primary-foreground shadow-xs h-10 has-[>svg]:px-4 rounded-none text-lg px-8 py-6 uppercase tracking-widest font-bold group inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary text-primary-foreground shadow-xs h-10 has-[>svg]:px-4 rounded-none text-lg px-8 py-6 uppercase tracking-widest font-bold group inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary text-primary-foreground shadow-xs h-10 has-[>svg]:px-4 rounded-none text-lg px-8 py-6 uppercase tracking-widest font-bold group !w-[311.047px] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">
                Consult Our Experts
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-2" />
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}